import { createFileRoute } from "@tanstack/react-router";
import { SeoPage } from "@/components/SeoPage";
import { faqJsonLd } from "@/lib/seo-jsonld";
import { breadcrumbJsonLd } from "@/lib/seo-jsonld";
import { KOMMUN_LINKS, SOURCE_FOHM_VAGLEDNING, SOURCE_ALKOHOLLAGEN, LAST_REVIEWED } from "@/lib/official-sources";

const faqs = [
  { q: "Vart ansöker jag om serveringstillstånd i Örebro?", a: "Ansökan görs till Socialnämnden / tillståndsenheten i Örebro kommun. Verksamhet i annan kommun i Örebro län (t.ex. Kumla, Hallsberg, Lekeberg, Nora) söker hos respektive kommun." },
  { q: "Vad kostar serveringstillstånd i Örebro?", a: "Örebro kommun tar ut en ansökningsavgift och en årlig tillsynsavgift. Avgifterna varierar beroende på tillståndstyp och alkoholförsäljningens storlek – kontrollera aktuella avgifter på kommunens webbplats." },
  { q: "Hur lång är handläggningstiden i Örebro?", a: "Örebro kommun anger normalt 8–12 veckor för en komplett ansökan om stadigvarande tillstånd. Tillfälliga tillstånd kan gå snabbare." },
];

export const Route = createFileRoute("/serveringstillstand-orebro")({
  component: () => (
    <SeoPage
      breadcrumbs={[
        { label: "Hem", to: "/" },
        { label: "Guider", to: "/serveringstillstand-och-kunskapsprov" },
        { label: "Örebro" },
      ]}
      title="Serveringstillstånd Örebro – ansökan och krav"
      intro="Allt du behöver veta om serveringstillstånd i Örebro: var du ansöker, vilka krav som ställs och hur du förbereder dig för kunskapsprovet."
      lastReviewed={LAST_REVIEWED}
      sources={[KOMMUN_LINKS.orebro, SOURCE_FOHM_VAGLEDNING, SOURCE_ALKOHOLLAGEN]}
      localData={{
        unit: "Örebro kommun – Miljö- och stadsbyggnadsförvaltningen, Miljöavdelningen",
        address: "Östra Bangatan 7A, Box 33400, 701 35 Örebro",
        phone: "019-21 10 00 (Örebro kommuns servicecenter)",
        waitTime: "ca 8–12 veckor för stadigvarande tillstånd från komplett ansökan",
        feeExamples: [
          "Ny ansökan om stadigvarande tillstånd: ca 10 000 kr",
          "Ändrat tillstånd: ca 3 000 kr",
          "Tillfälligt tillstånd till allmänheten (enstaka tillfälle): ca 3 000 kr, eller ca 1 000 kr vid obetydlig omfattning (max 50 personer)",
          "Rörlig tillsynsavgift: från ca 3 600 kr/år beroende på alkoholomsättning",
        ],
        checklist: [
          "Registreringsbevis",
          "Finansieringsplan och kontoutdrag",
          "Hyresavtal eller nyttjanderätt till lokalen",
          "Ritning över serveringsyta",
          "Brandskyddsdokumentation",
          "PBI-uppgifter och godkänt kunskapsprov",
        ],
        officialUrl: KOMMUN_LINKS.orebro,
      }}
      sections={[
        { h2: "Var ansöker du i Örebro?", body: "Socialnämnden / tillståndsenheten i Örebro kommun ansvarar för serveringstillstånd i Örebro kommun. För verksamhet i kringliggande kommuner som Kumla, Hallsberg, Lekeberg, Nora ansöker du hos respektive kommun. Varje kommun har egna blanketter och e-tjänster, men kraven enligt alkohollagen är nationella och gäller överallt." },
        { h2: "Krav från Örebro kommun", body: "Utöver de nationella kraven (personlig och ekonomisk lämplighet, godkänt kunskapsprov och lämplig lokal) kan kommunen ha lokala riktlinjer kring serveringstider, uteservering och ordning. Från 1 juni 2026 är kraven på eget kök och varierat matutbud borttagna nationellt. Örebro har särskilda riktlinjer för uteserveringar i innerstaden och kring Slottet." },
        { h2: "Kunskapsprovet i Örebro", body: "Provet genomförs hos kommunen efter att ansökan har kommit in. Det är ett skriftligt prov inom alkohollagen, serveringsbestämmelser, ordning och nykterhet, marknadsföring m.m. Frågeantalet varierar beroende på tillståndstyp och kommun – Folkhälsomyndighetens föreskrifter FoHMFS 2014:7 anger områden och miniminivåer. Med Alkoholprovet kan du träna i samma format hemifrån och gå in på provet förberedd." },
        { h2: "Avgifter och handläggningstid i Örebro", body: "Örebro kommuns miljöavdelning tar ut ca 10 000 kr för en ny ansökan om stadigvarande serverings-, provsmaknings- eller gårdsförsäljningstillstånd, medan ett tillfälligt tillstånd till allmänheten normalt kostar ca 3 000 kr per tillfälle. Den rörliga tillsynsavgiften trappas upp med alkoholomsättningen. Räkna med 8–12 veckor från komplett ansökan till beslut om stadigvarande tillstånd; kontakta gärna kommunens servicecenter på 019-21 10 00 vid frågor om din pågående ansökan." },
        { h2: "Tillsyn och uppföljning", body: "Örebro kommun bedriver aktiv tillsyn på serveringsställen tillsammans med polis och Skatteverket. Anmärkningar kan leda till erinran, varning, sanktionsavgift eller återkallat tillstånd. Goda kunskaper i alkohollagen minskar risken för misstag i den dagliga driften." },
      ]}
      faqs={faqs}
      related={{
        title: "Relaterade sidor",
        links: [
          { to: "/serveringstillstand-och-kunskapsprov", label: "Hub – alla guider" },
          { to: "/serveringstillstand-stockholm", label: "Serveringstillstånd Stockholm" },
          { to: "/serveringstillstand-goteborg", label: "Serveringstillstånd Göteborg" },
          { to: "/serveringstillstand-malmo", label: "Serveringstillstånd Malmö" },
          { to: "/serveringstillstand-restaurang", label: "För restaurang" },
          { to: "/serveringstillstand-bar", label: "För bar & nattklubb" },
          { to: "/tillfalligt-serveringstillstand", label: "Tillfälligt tillstånd" },
          { to: "/kunskapsprov-alkohollagen", label: "Kunskapsprov i alkohollagen" },
          { to: "/foretag", label: "Företagslösning" },
        ],
      }}
    />
  ),
  head: () => ({
    meta: [
      { title: "Serveringstillstånd i Örebro – avgifter & handläggning" },
      { name: "description", content: "Allt om hur du söker serveringstillstånd i Örebro: krav, kostnader och förberedelse inför provet i alkohollagen." },
      { property: "og:title", content: "Serveringstillstånd – Örebro" },
      { property: "og:description", content: "Ansökan, krav, avgifter och kunskapsprov för serveringstillstånd i Örebro." },
      { property: "og:url", content: "https://alkoholprovet.se/serveringstillstand-orebro" },
    ],
    links: [{ rel: "canonical", href: "https://alkoholprovet.se/serveringstillstand-orebro" }],
    scripts: [
      faqJsonLd(faqs),
      breadcrumbJsonLd([
        { label: "Hem", url: "https://alkoholprovet.se/" },
        { label: "Guider", url: "https://alkoholprovet.se/serveringstillstand-och-kunskapsprov" },
        { label: "Örebro", url: "https://alkoholprovet.se/serveringstillstand-orebro" },
      ]),
    ],
  }),
});
